import React from 'react';
import { useSelector } from 'react-redux';
import { TripState } from '../../types/trip.store.type';
import { BedTypes, BathroomTypes } from '../../types/trip.type';
import { HomeInfo } from 'guest-data-types-ts-lib';
import './HomeEssentials.scss';
import { IconPeopleTwo, IconBed, IconBathtub, IconHouse } from '../../assets/icons';
import { TranslationUtils } from '../../utils';
import { useFeature } from '@optimizely/react-sdk';
import { Splits } from '../../types/split.type';
import _ from 'lodash';

export const HomeEssentials: React.FC = () => {
    const { home_info } = useSelector((store: { trip: TripState }) => store.trip);

    const homeBedrooms = home_info?.bedrooms_summary_text;
    const getHomeBeds = (home: HomeInfo): string => {
        const beds = [];

        for (const type in BedTypes) {
            const bedsForType = (home as any)[type];
            if (bedsForType > 0) {
                const slug = BedTypes[type];
                const bedTypeName = TranslationUtils.get(`${bedsForType > 1 ? `${slug}_plural` : slug}`);
                beds.push(`${bedsForType} ${bedTypeName}`);
            }
        }

        if (beds.length === 0) {
            beds.push(TranslationUtils.get('no_beds'));
        }

        return beds.join(', ');
    };

    const getHomeBathrooms = (home: HomeInfo): string => {
        const bathrooms = [];

        for (const type in BathroomTypes) {
            const bathroomsForType = (home as any)[type];
            if (bathroomsForType > 0) {
                const slug = BathroomTypes[type];
                const bathTypeName = TranslationUtils.get(`${bathroomsForType > 1 ? `${slug}_plural` : slug}`);
                bathrooms.push(`${bathroomsForType} ${bathTypeName}`);
            }
        }
        if (bathrooms.length === 0) {
            bathrooms.push(TranslationUtils.get('no_bathrooms'));
        }
        return bathrooms.join(', ');
    };

    const unit = useSelector((store: { trip: TripState }) => store.trip.unit);
    const homeInfo = useSelector((store: { trip: TripState }) => store.trip.home_info);

    if (!homeInfo || !unit) {
        return null;
    }

    const { unit_area, max_occupancy } = unit;

    const shouldShowUnitArea = !!(unit_area.unit_of_measurement && unit_area.unit_of_measurement);

    const homeBeds = getHomeBeds(homeInfo);
    const homeBathrooms = getHomeBathrooms(homeInfo);

    return (
        <React.Fragment>
            <p tabIndex={0} className="page-subtitle">
                {TranslationUtils.get('home_essentials')}
            </p>
            <div tabIndex={0} className="panel">
                <div className="row divider-row home-essentials">
                    <div className={`col-6 ${shouldShowUnitArea ? 'col-sm-3' : 'col-sm-4'}`}>
                        <div className="essential">
                            <IconPeopleTwo className="essential-icon" />
                            <p className={'body-xs-bold'}>
                                {TranslationUtils.get('max_occupancy')}
                                {` ${max_occupancy}`}
                            </p>
                        </div>
                    </div>

                    <div className={`col-6 ${shouldShowUnitArea ? 'col-sm-3' : 'col-sm-4'}`}>
                        <div className="essential">
                            <IconBed className="essential-icon" />
                            {!_.isNil(homeBedrooms) ? (
                                <p className={'body-xs-bold'}>{homeBedrooms}</p>
                            ) : (
                                <p className={'body-xs-bold'}>{homeBeds}</p>
                            )}
                        </div>
                    </div>

                    <div className={`${shouldShowUnitArea ? 'col-6 col-sm-3' : 'col-12 col-sm-4'}`}>
                        <div className="essential">
                            <IconBathtub className="essential-icon" />
                            <p className={'body-xs-bold'}>{homeBathrooms}</p>
                        </div>
                    </div>

                    {shouldShowUnitArea && (
                        <div className="col-6 col-sm-3">
                            <div className="essential">
                                <IconHouse className="essential-icon" />
                                <p className={'body-xs-bold'}>{`${unit_area.area} ${TranslationUtils.get(
                                    `${unit_area.unit_of_measurement}`
                                )}`}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </React.Fragment>
    );
};
